import { Appointment, BookingFlowOption } from 'generated/api';
import { APPOINTMENT_STATUS } from 'services/AppointmentUtils';

/**
 * Concern names (normalized to lowercase) that count as a physical.
 *
 * Matched exactly rather than by substring: a loose /physical/i would also hit
 * "Physical Therapy", and reporting a physio visit to a patient as their last
 * physical is a wrong clinical fact, not a cosmetic miss.
 *
 * If the clinic renames the concern in Booking Flow Options, this list is the
 * one place to update — nothing else in the app derives the name.
 */
export const PHYSICAL_CONCERN_NAMES = ['annual physical'];

export interface LastPhysical {
  /** Unix seconds, matching Appointment.date. */
  date: number;
  /** The concern name as configured, for display/tooltip. */
  concern: string;
}

function isPhysicalConcern(text?: string): boolean {
  return !!text && PHYSICAL_CONCERN_NAMES.includes(text.trim().toLowerCase());
}

/**
 * Resolve an appointment's concern name. Appointments usually carry the
 * denormalized `concern_text`; older ones only reference `concern_id`, so fall
 * back to the booking-flow concern list (same two-step used by CancellationList).
 */
function getConcernName(
  appointment: Appointment,
  bookingOptions: BookingFlowOption[] = [],
): string | undefined {
  if (appointment.concern_text) return appointment.concern_text;
  if (!appointment.concern_id) return undefined;

  return bookingOptions
    .map(({ concerns = [] }) => concerns.find(({ id }) => id === appointment.concern_id))
    .find((concern) => !!concern)?.text;
}

/**
 * The patient's most recent physical, or undefined if they've never had one
 * recorded as a booked appointment.
 *
 * Counts an appointment when it is in the past and was not cancelled or
 * no-showed — deliberately *not* `status === 'Completed'`. A physical from
 * years ago at a clinic that never closed the appointment out is still a
 * physical that happened, and silently dropping it would be worse than
 * occasionally counting a visit the patient skipped without telling anyone.
 *
 * @param now Unix seconds; injectable so tests are deterministic.
 */
export function getLastPhysical(
  appointments: Appointment[] = [],
  bookingOptions: BookingFlowOption[] = [],
  now: number = Math.floor(Date.now() / 1000),
): LastPhysical | undefined {
  const { cancelled, no_show } = APPOINTMENT_STATUS;

  return (appointments ?? [])
    .filter((appointment) => {
      if (!appointment?.date || appointment.date > now) return false;
      if (appointment.status === cancelled || appointment.status === no_show) return false;
      return isPhysicalConcern(getConcernName(appointment, bookingOptions));
    })
    .reduce<LastPhysical | undefined>((latest, appointment) => {
      if (latest && latest.date >= appointment.date!) return latest;
      return {
        date: appointment.date!,
        concern: getConcernName(appointment, bookingOptions)!,
      };
    }, undefined);
}
